import { get, postJsonAdmin } from './_core.js';

export interface SimulatorPersona {
  slug: string;
  name: string;
  target_module: string;
  cefr: string;
  native_language: string;
  country: string;
  age: number;
  has_free_form: boolean;
}

export interface SimulatorScenario {
  slug: string;
  title: string;
  mode: 'task' | 'free_form';
  applicable_modules: string[];
  applicable_levels: string[];
  max_turns: number;
}

export interface SimulatorModelOption {
  id: string;
  provider: string;
  displayName: string;
  status: 'stable' | 'experimental' | 'deprecated';
  tier: string;
}

export interface SimulatorCatalog {
  personas: SimulatorPersona[];
  scenarios: SimulatorScenario[];
  simulatorModels: SimulatorModelOption[];
  /** `null` when the backend's default simulator model is not wired: pick one. */
  defaultModel: string | null;
}

export interface SimulatorBatchSummary {
  id: string;
  triggered_at: string;
  finished_at: string | null;
  status: string;
  sessions_total: number;
  sessions_completed: number;
  sessions_failed: number;
  total_cost_nano: string;
}

export interface SimulatorRunDetail {
  id: string;
  session_id: string;
  persona_slug: string;
  scenario_slug: string;
  module_slug: string;
  status: string;
  end_reason: string | null;
  turn_count: number;
  simulator_total_input_tokens: number;
  simulator_total_output_tokens: number;
  started_at: string;
  ended_at: string | null;
  error_text: string | null;
}

export interface SimulatorBatchDetail {
  batch: Record<string, unknown>;
  runs: SimulatorRunDetail[];
}

export interface SimulatorBatchRequest {
  combinations: Array<{ personaSlug: string; scenarioSlug: string; count?: number }>;
  concurrency?: number;
  seed?: number;
  simulatorModel?: string;
  /** Fase 39 — opt-out flags (defaults backend: skipTts/Analyze=true, skipDistill=false). */
  skipTts?: boolean;
  skipAnalyze?: boolean;
  skipDistill?: boolean;
}

export interface SimulatorCostEstimateBreakdown {
  personaSlug: string;
  scenarioSlug: string;
  count: number;
  source: 'historical' | 'fallback';
  historicalRuns: number;
  avgTurns: number;
  avgInPerTurn: number;
  avgOutPerTurn: number;
}

export interface SimulatorCostEstimate {
  totalSessions: number;
  approxTurns: number;
  simulatorCostUsd: number;
  agentCostUsd: number | null;
  totalCostUsd: number | null;
  breakdown: SimulatorCostEstimateBreakdown[];
  note: string;
}

export async function fetchSimulatorCatalog(): Promise<SimulatorCatalog> {
  const r = await get<SimulatorCatalog>('/admin/simulator/catalog');
  return r.data;
}

export async function fetchSimulatorBatches(): Promise<SimulatorBatchSummary[]> {
  const r = await get<{ batches: SimulatorBatchSummary[] }>('/admin/simulator/batches');
  return r.data.batches;
}

export async function fetchSimulatorBatch(id: string): Promise<SimulatorBatchDetail> {
  const r = await get<SimulatorBatchDetail>(`/admin/simulator/batches/${id}`);
  return r.data;
}

export async function triggerSimulatorBatch(
  spec: SimulatorBatchRequest,
): Promise<{ triggered: boolean }> {
  return postJsonAdmin<{ triggered: boolean }>('/admin/simulator/batches', spec);
}

export async function estimateSimulatorBatchCost(
  spec: SimulatorBatchRequest,
): Promise<SimulatorCostEstimate> {
  return postJsonAdmin<SimulatorCostEstimate>('/admin/simulator/cost-estimate', spec);
}

export async function resetSimulatorPersona(slug: string): Promise<{ sessionsDeleted: number; factsDeleted: number }> {
  return postJsonAdmin<{ sessionsDeleted: number; factsDeleted: number }>(
    `/admin/simulator/personas/${slug}/reset?confirm=true`,
    {},
  );
}

export async function cancelSimulatorBatch(id: string): Promise<{ cancelled: boolean; batchId: string }> {
  return postJsonAdmin<{ cancelled: boolean; batchId: string }>(
    `/admin/simulator/batches/${id}/cancel`,
    {},
  );
}
